import React, { useState, useEffect } from 'react';
import { Icon } from './icons';
import { BookV2 } from '../types';

/* ProgressRing
 *
 * Modo único (default): preenche um único arco com `value` (0..1) na cor `color`.
 * Modo multi: passa `segments=[{ value, color }]` — desenha um arco por segmento,
 *   cada um começando onde o anterior terminou. Útil pra "pizza" de progresso
 *   por livro. Soma dos values pode passar de 1; o excedente é cropado.
 *
 * `scale` (default 1): aplica transform: scale APENAS no <svg>, deixando o
 * conteúdo de texto (children, posicionado absolute na wrapper) intacto. Útil
 * pra "expandir o anel sem deformar os números do meio".
 *
 * `spinTurns` (default 0): cada incremento adiciona +360° à rotação base de -90°.
 * Como CSS interpola transform linearmente, indo de spinTurns N pra N+1 produz
 * uma volta completa clockwise (efeito "fluido de progresso"). Manter o valor
 * estável durante collapse evita spin reverso.
 */
export const ProgressRing: React.FC<{
  value?: number;
  segments?: { value: number; color: string }[];
  size?: number; stroke?: number; color?: string; trackColor?: string;
  scale?: number;
  spinTurns?: number;
  children?: React.ReactNode;
  onClick?: () => void;
}> = ({ value = 0, segments, size = 120, stroke = 10, color, trackColor, scale = 1, spinTurns = 0, children, onClick }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  // Modo multi-segmento — cada segmento é um <circle> com strokeDasharray + offset.
  // strokeDashoffset NEGATIVO empurra o início do dash forward (clockwise).
  const useMulti = !!(segments && segments.length > 0);

  // Rotação base do .ring é -90deg (12 o'clock como zero). spinTurns adiciona
  // voltas inteiras: cada +1 = +360°. Visualmente para no mesmo ponto, mas o
  // browser interpolando o transform produz a animação de uma volta clockwise.
  const totalRotate = -90 + 360 * spinTurns;

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative', width: size, height: size,
        cursor: onClick ? 'pointer' : 'default',
      }}>
      {/* SVG escala+rotaciona isolado — text overlay fica fora do <svg> e não é afetado. */}
      <svg width={size} height={size} className="ring" style={{
        transform: `rotate(${totalRotate}deg) scale(${scale})`,
        transformOrigin: 'center center',
        transition: 'transform 700ms cubic-bezier(.2,.8,.2,1)',
      }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={trackColor || 'var(--paper-3)'} strokeWidth={stroke} />

        {useMulti ? (() => {
          let acc = 0;
          return segments!.map((s, i) => {
            const v = Math.max(0, s.value);
            // Crop pra não passar de 1 no acumulado (evita overlap visual)
            const clipped = Math.max(0, Math.min(1 - acc, v));
            const len = clipped * c;
            const off = -acc * c;
            acc += clipped;
            // strokeDasharray "len resto" + linecap butt nas pontas que ENCONTRAM
            // outro segmento, e round só na ponta solta — pra ficar como pizza limpa
            // sem hover entre segmentos. Como SVG não tem linecap por ponta, usamos
            // butt sempre no multi (transição segura). Round só na última do final.
            const isLast = i === segments!.length - 1;
            const isAtEnd = acc >= 0.999;
            return (
              <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
                stroke={s.color} strokeWidth={stroke}
                strokeLinecap={isLast && !isAtEnd ? 'round' : 'butt'}
                strokeDasharray={`${len} ${c - len + 0.001}`}
                strokeDashoffset={off}
                style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(.2,.8,.2,1), stroke-dasharray 600ms cubic-bezier(.2,.8,.2,1), stroke 400ms' }}
              />
            );
          });
        })() : (
          <circle cx={size/2} cy={size/2} r={r} fill="none"
            stroke={color || 'var(--ember)'} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c - (Math.min(Math.max(value, 0), 1) * c)}
            style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(.2,.8,.2,1), stroke 400ms' }} />
        )}
      </svg>
      {children && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column',
          pointerEvents: 'none',
        }}>{children}</div>
      )}
    </div>
  );
};

/* Flame — streak icon, scales with count */
export const Flame: React.FC<{ count?: number; size?: number }> = ({ count = 0, size = 34 }) => {
  const intensity = Math.min(1, count / 20);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <svg width={size} height={size} viewBox="0 0 40 48" className="flame">
        <defs>
          <linearGradient id={`fl1-${size}-${count}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="var(--ember)"/>
            <stop offset="1" stopColor="#F4B041"/>
          </linearGradient>
          <linearGradient id={`fl2-${size}-${count}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0" stopColor="#FFD580"/>
            <stop offset="1" stopColor="#FFF1CC"/>
          </linearGradient>
        </defs>
        <path d="M20 46c9 0 15-6 15-14 0-6-4-10-6-14-2 4-4 6-6 6-2-4-4-8-2-14-6 4-14 10-14 22 0 8 4 14 13 14z"
          fill={`url(#fl1-${size}-${count})`} opacity={0.35 + 0.65*intensity}/>
        <path d="M20 42c5 0 8-3 8-8 0-3-2-5-3-7-1 2-2 3-3 3-1-2-2-4-1-7-3 2-7 5-7 11 0 5 3 8 6 8z"
          fill={`url(#fl2-${size}-${count})`}/>
      </svg>
    </div>
  );
};

/* BookCover — renders from hue/style or falls back to coverUrl */
/** Imagem de capa que, se falhar (os serviços de capa dão 504), mostra o
 *  quadro padrão em vez do ícone de imagem quebrada (pente fino, UX-32). */
export const ImgCapa: React.FC<React.ImgHTMLAttributes<HTMLImageElement> & { fallback?: React.ReactNode }> = ({ fallback = null, src, ...rest }) => {
  const [falhou, setFalhou] = useState(false);
  useEffect(() => { setFalhou(false); }, [src]);
  if (!src || falhou) return <>{fallback}</>;
  // eslint-disable-next-line jsx-a11y/alt-text
  return <img src={src} {...rest} onError={() => setFalhou(true)} />;
};

export const BookCover: React.FC<{ book: BookV2; width?: number; height?: number }> = ({ book, width = 64, height = 96 }) => {
  const hue = book.coverHue ?? 20;
  const style = book.coverStyle || 'paper';
  const bg1 = `oklch(0.78 0.09 ${hue})`;
  const bg2 = `oklch(0.62 0.11 ${hue})`;
  const stripe = `oklch(0.55 0.08 ${hue})`;
  // Sem título (o livro ainda é só { id }: chegou por link antes de a estante
  // carregar), a capa desenha vazia em vez de derrubar o app (pente fino, MOB-19).
  const titleWords = String(book.title || '').split(' ');

  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [book.coverUrl]);

  const hasRealCover = !!book.coverUrl && book.coverUrl !== 'null' && !imgFailed;

  return (
    <div style={{
      width, height, borderRadius: 4, position: 'relative', overflow: 'hidden',
      // O desenho de fallback fica SEMPRE por baixo. Enquanto a capa real está
      // baixando — ou se o arquivo sumiu — o que aparece é o livro estilizado,
      // nunca um retângulo vazio. Com a capa de 46px isso passava batido; no
      // card da Home ela tem 132x198 e o buraco é o tamanho do herói.
      background: `linear-gradient(135deg, ${bg1}, ${bg2})`,
      boxShadow: '0 12px 22px -10px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.08), inset 2px 0 0 rgba(0,0,0,0.12), inset -1px 0 0 rgba(255,255,255,0.12)',
      flexShrink: 0,
    }}>
      {hasRealCover && (
        <img src={book.coverUrl!} alt={book.title}
          style={{ position: 'absolute', inset: 0, zIndex: 1, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={() => setImgFailed(true)}
          />
      )}
      <>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: 'linear-gradient(90deg, rgba(0,0,0,0.25), rgba(0,0,0,0) 100%)' }} />
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 3, background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(255,255,255,0.2) 100%)' }} />
        {style === 'linen' && (
          <div style={{ position: 'absolute', inset: 0, opacity: 0.3,
            backgroundImage: `repeating-linear-gradient(45deg, ${stripe}22 0 1px, transparent 1px 4px), repeating-linear-gradient(-45deg, ${stripe}22 0 1px, transparent 1px 4px)`,
          }} />
        )}
        {style === 'cloth' && (
          <div style={{ position: 'absolute', inset: 0, opacity: 0.2,
            backgroundImage: `radial-gradient(${stripe}55 0.5px, transparent 0.5px)`,
            backgroundSize: '3px 3px',
          }} />
        )}
        <div style={{
          position: 'absolute', left: 10, right: 8, top: 10,
          fontFamily: 'Instrument Serif, serif',
          fontSize: width > 80 ? 12 : 9, lineHeight: 1.1,
          color: 'rgba(255,255,255,0.92)', fontWeight: 400,
        }}>
          {titleWords.slice(0, 4).join(' ')}
        </div>
        <div style={{
          position: 'absolute', left: 10, right: 8, bottom: 8,
          fontFamily: 'Geist Mono, monospace',
          fontSize: width > 80 ? 7 : 5.5,
          color: 'rgba(255,255,255,0.7)',
          textTransform: 'uppercase', letterSpacing: '0.1em',
        }}>
          {book.author}
        </div>
        <div style={{
          position: 'absolute', left: '50%', bottom: width > 80 ? 28 : 20,
          transform: 'translateX(-50%)',
          width: width > 80 ? 20 : 14, height: 1,
          background: 'rgba(255,255,255,0.4)',
        }} />
      </>
    </div>
  );
};

/* BookListRow */
export const BookListRow: React.FC<{ book: BookV2; onClick: () => void; onActions?: () => void }> = ({ book, onClick, onActions }) => {
  const pct = book.pages ? Math.round((book.read / book.pages) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
    <button onClick={onClick} style={{
      display: 'flex', gap: 14, flex: 1, minWidth: 0, textAlign: 'left',
      padding: onActions ? '14px 4px 14px 20px' : '14px 20px', alignItems: 'center', background: 'transparent',
    }}>
      <BookCover book={book} width={54} height={80} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 20, lineHeight: 1.15, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {book.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
          {book.author}
        </div>
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'var(--paper-3)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: book.status === 'finished' ? 'var(--sage)' : 'var(--ember)',
              transition: 'width 600ms',
            }} />
          </div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{pct}%</div>
        </div>
      </div>
    </button>
    {onActions && (
      <button onClick={onActions} aria-label="Mais opções" style={{
        width: 36, height: 36, borderRadius: '50%', marginRight: 12, flexShrink: 0,
        background: 'transparent', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)',
      }}><Icon name="dots" size={18} color="var(--ink-3)"/></button>
    )}
    </div>
  );
};

/* TopBar */
export const TopBar: React.FC<{ onBack?: () => void; title?: string; trailing?: React.ReactNode; minimal?: boolean }> = ({ onBack, title, trailing, minimal }) => (
  <div style={{
    paddingTop: 40, padding: '40px 20px 0',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    minHeight: 58,
  }}>
    {onBack ? (
      <button onClick={onBack} aria-label="Voltar" style={{
        width: 38, height: 38, borderRadius: '50%',
        background: 'var(--paper-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid var(--line)',
      }}>
        <Icon name="chevron-left" size={18} />
      </button>
    ) : <div style={{ width: 38 }} />}
    {!minimal && title && (
      <div style={{ fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
        {title}
      </div>
    )}
    <div style={{ display: 'flex', gap: 8 }}>
      {trailing || <div style={{ width: 38 }} />}
    </div>
  </div>
);

/* Toggle */
export const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button role="switch" aria-checked={on} onClick={() => onChange(!on)} style={{
    width: 44, height: 26, borderRadius: 13,
    background: on ? 'var(--ember)' : 'var(--paper-3)',
    position: 'relative', transition: 'background 200ms',
    flexShrink: 0,
  }}>
    <span style={{
      position: 'absolute', top: 3, left: on ? 21 : 3,
      width: 20, height: 20, borderRadius: '50%',
      background: 'var(--paper)',
      transition: 'left 220ms cubic-bezier(.2,.8,.2,1)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    }} />
  </button>
);

/* TabBar — bottom nav.
   Substituição (única permitida pelo Wesley): "Livros" foi substituído por "Clube/Grupo".
   A label do segundo botão acompanha o termo escolhido pelo usuário (clube por padrão).
   Library segue acessível via Profile → "Minha estante".

   Suporta navegação SPA via prop opcional `urls`: se passada, cada tab vira um
   `<a href>` que respeita cmd+click / middle-click pra abrir em nova aba, e o
   click normal é interceptado e roteado pelo onChange (preventDefault). Sem
   `urls` mantém comportamento legado de button puro. */
export const TabBar: React.FC<{
  current: string;
  onChange: (id: string) => void;
  onAdd: () => void;
  clubsTerm?: 'clube' | 'grupo';
  urls?: Record<string, string>;
  /** Anima pulso no botão central pra destacar pra usuário novo (sem livros). */
  pulseAdd?: boolean;
}> = ({ current, onChange, onAdd, clubsTerm = 'clube', urls, pulseAdd = false }) => {
  const clubLabel = clubsTerm === 'grupo' ? 'Grupo' : 'Clube';
  const tabs = [
    { id: 'home',  label: 'Hoje',     icon: 'home' },
    { id: 'clubs', label: clubLabel,  icon: 'users' },
    { id: 'add',   label: '',         icon: 'plus', center: true },
    { id: 'explore', label: 'Explorar', icon: 'search' },
    { id: 'me',    label: 'Eu',       icon: 'user' },
  ] as const;

  const renderInner = (t: typeof tabs[number], active: boolean) => (
    <>
      <Icon name={t.icon} size={20} color={active ? 'var(--ember)' : 'var(--ink-4)'} stroke={active ? 2 : 1.6} />
      <span>{t.label}</span>
    </>
  );

  return (
    <div className="tabbar">
      {tabs.map(t => {
        if ((t as any).center) {
          return (
            <button
              key={t.id}
              className={`tab-center ${pulseAdd ? 'pulse-add' : ''}`}
              onClick={onAdd}
              data-tour="tab-add"
              aria-label="Adicionar livro"
            >
              <Icon name="plus" size={22} color="var(--paper)" stroke={2} />
              {pulseAdd && (
                <style>{`
                  .tab-center.pulse-add::before {
                    content: '';
                    position: absolute;
                    inset: -4px;
                    border-radius: 50%;
                    border: 2px solid var(--ember);
                    opacity: 0;
                    animation: tabPulse 2s ease-out infinite;
                    pointer-events: none;
                  }
                  @keyframes tabPulse {
                    0%   { opacity: 0.6; transform: scale(0.85); }
                    70%  { opacity: 0;   transform: scale(1.35); }
                    100% { opacity: 0;   transform: scale(1.4);  }
                  }
                `}</style>
              )}
            </button>
          );
        }
        const active = current === t.id;
        const href = urls?.[t.id];
        if (href) {
          return (
            <a
              key={t.id}
              href={href}
              className={`tab ${active ? 'active' : ''}`}
              data-tour={`tab-${t.id}`}
              onClick={(e) => {
                // Permite cmd/ctrl/shift+click ou middle-click abrirem nova aba (browser default)
                if (e.metaKey || e.ctrlKey || e.shiftKey || (e as any).button === 1) return;
                e.preventDefault();
                onChange(t.id);
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {renderInner(t, active)}
            </a>
          );
        }
        return (
          <button key={t.id} className={`tab ${active ? 'active' : ''}`} onClick={() => onChange(t.id)} data-tour={`tab-${t.id}`}>
            {renderInner(t, active)}
          </button>
        );
      })}
    </div>
  );
};

/* Sheet */
export const Sheet: React.FC<{ open: boolean; onClose: () => void; children: React.ReactNode; title?: string }> = ({ open, onClose, children, title }) => (
  <>
    <div className={`sheet-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
    <div className={`sheet ${open ? 'open' : ''}`}>
      <div className="sheet-grabber" />
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 26 }}>{title}</div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--paper-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
      {children}
    </div>
  </>
);

/* FlameBar — 3 states on the last streak bar */
export const FlameBar: React.FC<{ state?: 'off' | 'burning' | 'roaring' }> = ({ state = 'burning' }) => {
  const off = state === 'off';
  const roaring = state === 'roaring';
  const stubH = 10;
  const barW = 5;
  const flameW = roaring ? 30 : 22;
  const flameH = roaring ? 46 : (off ? 0 : 32);

  return (
    <div style={{
      position: 'relative',
      width: barW, height: stubH + (off ? 0 : flameH),
      display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
      transition: 'height 500ms cubic-bezier(.2,.8,.2,1)',
    }}>
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        width: barW, height: stubH, borderRadius: 2,
        background: off ? 'var(--paper-3)' : 'linear-gradient(180deg, var(--ember) 0%, #8a2f11 100%)',
        boxShadow: off ? 'none' : '0 0 6px rgba(199,83,26,0.6)',
        zIndex: 2,
        transition: 'background 400ms',
      }}/>
      {!off && (
        <svg width={flameW} height={flameH} viewBox="0 0 40 60" style={{
          position: 'absolute', bottom: stubH - 3, left: barW / 2 - flameW / 2,
          overflow: 'visible',
          transition: 'width 500ms cubic-bezier(.2,.8,.2,1), height 500ms cubic-bezier(.2,.8,.2,1), bottom 500ms',
          filter: roaring
            ? 'drop-shadow(0 0 10px rgba(244,176,65,0.8)) drop-shadow(0 0 20px rgba(199,83,26,0.6))'
            : 'drop-shadow(0 0 4px rgba(244,176,65,0.5))',
          animation: roaring ? 'flameWobble 600ms ease-in-out infinite' : 'flameWobble 1200ms ease-in-out infinite',
          transformOrigin: '50% 100%',
        }}>
          <defs>
            <radialGradient id={`flameOuter-${state}`} cx="50%" cy="90%" r="60%">
              <stop offset="0%" stopColor="#F4B041" stopOpacity="0.9"/>
              <stop offset="60%" stopColor="#C7531A" stopOpacity="0.7"/>
              <stop offset="100%" stopColor="#C7531A" stopOpacity="0"/>
            </radialGradient>
            <linearGradient id={`flameMid-${state}`} x1="0.5" y1="1" x2="0.5" y2="0">
              <stop offset="0%" stopColor="#E87A3B"/>
              <stop offset="60%" stopColor="#F4B041"/>
              <stop offset="100%" stopColor="#FFD580"/>
            </linearGradient>
            <linearGradient id={`flameCore-${state}`} x1="0.5" y1="1" x2="0.5" y2="0">
              <stop offset="0%" stopColor="#FFD580"/>
              <stop offset="70%" stopColor="#FFF1CC"/>
              <stop offset="100%" stopColor="#FFFFFF"/>
            </linearGradient>
          </defs>
          <ellipse cx="20" cy="50" rx={roaring ? 18 : 13} ry={roaring ? 22 : 15}
            fill={`url(#flameOuter-${state})`}
            style={{ animation: 'flameHalo 1.2s ease-in-out infinite alternate' }}/>
          <path d="M20 58 C10 52, 6 42, 10 32 C12 28, 15 26, 17 22 C18 26, 20 28, 21 28 C22 24, 21 18, 24 12 C28 18, 32 24, 32 34 C32 46, 28 54, 20 58 Z"
            fill={`url(#flameMid-${state})`}
            style={{ animation: 'flameDance 800ms ease-in-out infinite', transformOrigin: '50% 100%' }}/>
          <path d="M20 56 C15 52, 13 44, 16 36 C17 33, 19 32, 20 30 C21 32, 21 27, 23 22 C26 28, 28 36, 26 46 C25 52, 23 55, 20 56 Z"
            fill={`url(#flameCore-${state})`}
            style={{ animation: 'flameDanceInner 600ms ease-in-out infinite', transformOrigin: '50% 100%' }}
            opacity={roaring ? 1 : 0.85}/>
          <ellipse cx="20" cy="46" rx={roaring ? 2.5 : 2} ry={roaring ? 5 : 3.5}
            fill="#FFFFFF" opacity={0.7}
            style={{ animation: 'flameTip 500ms ease-in-out infinite' }}/>
          {roaring && (<>
            <circle cx="14" cy="12" r="1.2" fill="#F4B041" opacity="0.9">
              <animate attributeName="cy" values="22;0;22" dur="1.6s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0;1;0" dur="1.6s" repeatCount="indefinite"/>
            </circle>
            <circle cx="26" cy="16" r="1" fill="#FFD580" opacity="0.9">
              <animate attributeName="cy" values="26;2;26" dur="2.1s" repeatCount="indefinite" begin="0.4s"/>
              <animate attributeName="opacity" values="0;1;0" dur="2.1s" repeatCount="indefinite" begin="0.4s"/>
            </circle>
            <circle cx="20" cy="8" r="0.8" fill="#FFF1CC" opacity="0.9">
              <animate attributeName="cy" values="18;-6;18" dur="1.9s" repeatCount="indefinite" begin="0.9s"/>
              <animate attributeName="opacity" values="0;1;0" dur="1.9s" repeatCount="indefinite" begin="0.9s"/>
            </circle>
          </>)}
        </svg>
      )}
    </div>
  );
};

/* StreakTrail — bars for last 30 days */
export const StreakTrail: React.FC<{ history: { read: number }[]; todayState?: 'off' | 'burning' | 'roaring' }> = ({ history, todayState = 'burning' }) => {
  const trail = history.length ? history : Array(30).fill({ read: 0 });
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 44, position: 'relative', paddingRight: 4 }}>
      {trail.map((r, i) => {
        const isToday = i === trail.length - 1;
        if (isToday) return <FlameBar key={i} state={todayState}/>;
        const h = r.read ? 20 - ((i % 3) * 2) + (i % 3) * 2 : 5;
        const opacity = 0.25 + (i / trail.length) * 0.55;
        return (
          <div key={i} style={{
            width: 5, height: h, borderRadius: 2,
            background: r.read ? 'var(--ember)' : 'var(--paper-3)',
            opacity: r.read ? opacity : 1,
            transition: 'height 400ms',
          }} />
        );
      })}
    </div>
  );
};

/* Lockable — wraps any element, dims + shows lock when locked */
export const Lockable: React.FC<{
  locked: boolean; onUnlock?: () => void;
  children: React.ReactNode; variant?: 'card' | 'strip'; label?: string; style?: React.CSSProperties;
}> = ({ locked, onUnlock, children, variant = 'card', label, style }) => {
  if (!locked) return <>{children}</>;
  return (
    <div style={{ position: 'relative', ...style }}>
      <div style={{ filter: 'grayscale(0.7) brightness(0.55)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
      <button onClick={onUnlock} style={{
        position: 'absolute', inset: 0,
        background: 'rgba(10,8,7,0.35)',
        backdropFilter: 'blur(1px)', WebkitBackdropFilter: 'blur(1px)',
        border: 'none', borderRadius: variant === 'card' ? 18 : 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 8, cursor: 'pointer',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', background: 'var(--ember)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 18px -4px rgba(199,83,26,0.6)',
        }}>
          <Icon name="lock" size={20} color="var(--paper)" stroke={2}/>
        </div>
        {label && (
          <div style={{
            fontFamily: 'Instrument Serif, serif',
            fontSize: 16, fontStyle: 'italic',
            color: 'var(--paper)',
            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}>{label}</div>
        )}
      </button>
    </div>
  );
};

/* ProChip */
export const ProChip: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 8px',
    background: 'linear-gradient(135deg, var(--ember), #E87A3B)',
    color: 'var(--paper)', borderRadius: 999,
    fontFamily: 'Geist Mono, monospace',
    fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
    fontWeight: 600,
  }}>
    <Icon name="lock" size={9} color="currentColor" stroke={2.4}/>
    PRO
  </button>
);

/* PrimaryButton (used in auth) */
export const PrimaryButton: React.FC<{
  children: React.ReactNode; onClick: () => void; disabled?: boolean;
}> = ({ children, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: '100%', padding: '18px 24px',
    background: disabled ? 'var(--paper-3)' : 'var(--ink)',
    color: disabled ? 'var(--ink-4)' : 'var(--paper)',
    border: 'none', borderRadius: 999,
    fontSize: 15, fontWeight: 500,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'all 200ms',
  }}>{children}</button>
);

/* Editorial Field (auth forms) */
export const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; icon?: string; autoFocus?: boolean; inputMode?: any;
}> = ({ label, value, onChange, type = 'text', placeholder, icon, autoFocus, inputMode }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{
      borderBottom: `1px solid ${focused ? 'var(--ember)' : 'var(--line)'}`,
      paddingBottom: 8, transition: 'border-color 200ms',
    }}>
      <div className="mono" style={{
        fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: focused ? 'var(--ember)' : 'var(--ink-3)',
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        {icon && <Icon name={icon} size={16} color="var(--ink-3)"/>}
        <input
          type={type} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} autoFocus={autoFocus} inputMode={inputMode}
          autoComplete="off"
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            // Sans aqui de proposito: serif display emborrata @ . - em e-mail
            // e telefone, que sao exatamente os caracteres que a pessoa
            // precisa conferir antes de enviar. Titulo segue serif.
            fontSize: 16.5, fontFamily: "Geist, Geist Fallback, -apple-system, system-ui, sans-serif", color: 'var(--ink)',
            padding: '4px 0',
          }}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        />
      </div>
    </div>
  );
};
